import { flatten, unflatten } from 'flat';
import { v4 as uuidv4 } from 'uuid';
import { WebsocketProvider } from 'y-websocket';
import { IndexeddbPersistence } from 'y-indexeddb';
import * as Y from '../../shared/yjs';
import { WEBSOCKET_URL, getWsAuthParams } from '../../api/config';
import { buildIDBKey } from '../idb/idbKeyManager';
import { idbManifest } from '../idb/idbManifest';
import { shutdownCoordinator } from '../idb/shutdownCoordinator';
import { wsTokenRefresher } from '../wsTokenRefresher';

import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('CRDTSERVICEWS_SERVICE', { enabled: false });

export interface CRDTUpdate<T> {
  type: string;
  data: T;
  timestamp: number;
}

// Special known CRDT keys and their preferred debounce times
const CRDT_KEY_CONFIGS: Record<string, number> = {
  'default': 500,           // Default debounce time (ms)
  'cursor': 0,              // Cursor data: dispatch immediately — trailing debounce causes cursors to freeze during movement
  'presence': 0,            // Presence data: dispatch immediately for real-time cursor positions
  'window': 80,             // Window movements: debounce rapid drag events
  'window-position': 80,    // Window position updates
  'window-positions': 80,   // Window position updates (new format)
  'position': 80,           // Position data: matches window debounce
};

// Private instance variable
let _instance: CRDTServiceWS | null = null;

export class CRDTServiceWS {
  private docs = new Map<string, Y.Doc>();
  private providers = new Map<string, WebsocketProvider>();
  private connections: Map<string, (update: any) => void> = new Map();
  private observers: Map<string, { observer: any; cleanup: () => void }> = new Map();
  private states = new Map<string, Y.Map<any>>();
  private localOriginId = uuidv4();
  // IndexedDB persistence for space: rooms (offline-capable window layout)
  private indexedDbs = new Map<string, IndexeddbPersistence>();

  constructor() {
    shutdownCoordinator.register({
      name: 'crdtServiceWS:destroy',
      phase: 'destroy',
      handler: () => {
        try { this.cleanup(); } catch {}
      }
    });
  }

  public getYDoc(key: string): Y.Doc | undefined {
    return this.docs.get(key);
  }

  /**
   * Ensures the Y.Doc for `key` is initialized (creates it if needed, attaches IDB persistence)
   * and waits for IndexedDB to finish loading its persisted state before resolving.
   * For rooms without IDB persistence this resolves immediately.
   */
  public waitForIdbSynced(key: string): Promise<void> {
    this.ensureInitialized(key);
    const idb = this.indexedDbs.get(key);
    if (!idb) return Promise.resolve();
    return (idb as any).whenSynced.then(() => undefined);
  }

  public ensureInitialized(key: string, options?: { connectImmediately?: boolean }): Y.Doc {
    const wsTraceId = `${key.slice(-12)}_${Date.now()}`;
    
    log.log(`🔵 [WS_TRACE:${wsTraceId}] === CRDT-WS.ENSUREINITIALIZED ===`, {
      key,
      hasOptions: !!options,
      connectImmediately: options?.connectImmediately ?? true,
      existingDocsCount: this.docs.size,
      existingProvidersCount: this.providers.size
    });
    
    let doc = this.docs.get(key);
    let needsProvider = false;
    
    if (!doc) {
      log.log(`🔧 [WS_TRACE:${wsTraceId}] Creating NEW Y.Doc()...`);
      doc = new Y.Doc();
      this.docs.set(key, doc);
      needsProvider = true;
      log.log(`✅ [WS_TRACE:${wsTraceId}] New Y.Doc created:`, {
        clientId: doc.clientID,
        totalDocs: this.docs.size,
        needsProvider: true
      });
      log.log(`[CRDT-WS] Created new Y.Doc for key ${key}. Totals => docs:${this.docs.size}, providers:${this.providers.size}`);
    } else {
      needsProvider = !this.providers.has(key);
      log.log(`♻️ [WS_TRACE:${wsTraceId}] Reusing existing Y.Doc:`, {
        clientId: doc.clientID,
        needsProvider,
        totalDocs: this.docs.size
      });
      log.log(`[CRDT-WS] Reusing existing Y.Doc for key ${key}. Provider exists:${!needsProvider}. Totals => docs:${this.docs.size}, providers:${this.providers.size}`);
    }

    // Create provider if needed (either new doc or existing doc without provider)
    if (needsProvider) {
      const roomId = key;
      const connectImmediately = options?.connectImmediately ?? true;

      // ── IndexedDB persistence for space:, space-chat:, doc:, note:, terminal: rooms ──
      // Adds the offline layer so layout, per-user tab state, and document content
      // survive reload, tab close, and crashes. The WebSocket provider starts
      // DISCONNECTED and only connects after IndexedDB has loaded local state —
      // without this the provider broadcasts an empty Y.Doc to the server before IDB
      // has synced, which can overwrite persisted content.
      // space-chat:{spaceId}:{userId} is per-user so IDB keys are automatically
      // isolated (the full room name, including userId, is used as the IDB key).
      const needsIdb = !this.indexedDbs.has(roomId) && (
        roomId.startsWith('space:') || roomId.startsWith('space-chat:') ||
        roomId.startsWith('doc:') || roomId.startsWith('note:') || roomId.startsWith('terminal:')
      );
      if (needsIdb) {
        const idbType = (roomId.startsWith('space:') || roomId.startsWith('space-chat:'))
          ? 'space-layout' as const
          : 'room-crdt' as const;
        const idbKey = buildIDBKey(idbType, roomId);
        const idb = new IndexeddbPersistence(idbKey, doc);
        this.indexedDbs.set(roomId, idb);

        const entityId = roomId.includes(':') ? roomId.split(':')[1] : roomId;
        idbManifest.register(idbKey, idbType, entityId);
      }

      // Rooms with IDB must start disconnected; all other rooms use the
      // caller-supplied connectImmediately option.
      const shouldConnectImmediately = needsIdb ? false : connectImmediately;
      
      log.log(`🔧 [WS_TRACE:${wsTraceId}] Creating WebSocket provider:`, {
        roomId,
        connectImmediately: shouldConnectImmediately,
        wsUrl: WEBSOCKET_URL
      });
      
      try {
        const provider = new WebsocketProvider(WEBSOCKET_URL, roomId, doc, {
          connect: shouldConnectImmediately,
          params: getWsAuthParams(),
          resyncInterval: 30000,
          // Default maxBackoffTime is 2500ms — too aggressive during worker cycling.
          // With rapid successive drops the backoff counter resets on every successful open,
          // keeping retries at 100ms intervals and hammering the backend. 30s cap prevents this.
          maxBackoffTime: 30000,
        });
        
        log.log(`✅ [WS_TRACE:${wsTraceId}] Provider created:`, {
          connected: provider.wsconnected,
          synced: provider.synced,
          connectImmediately: shouldConnectImmediately
        });

        // Connect only after IndexedDB has loaded local content to avoid
        // broadcasting empty state to the server before IDB has synced.
        if (needsIdb) {
          const idb = this.indexedDbs.get(roomId)!;
          // Must match the idbType used at registration above.
          const idbKey = buildIDBKey(
            (roomId.startsWith('space:') || roomId.startsWith('space-chat:')) ? 'space-layout' : 'room-crdt',
            roomId
          );
          let preWsSyncSize = 0;
          idb.once('synced', () => {
            log.log(`[CRDT] 📦 IndexedDB synced for ${roomId.slice(-8)}, connecting WebSocket`);
            idbManifest.touchLastAccessed(idbKey);
            // Capture state size AFTER IDB loaded but BEFORE WS connects,
            // so the stale check compares IDB content vs. server content.
            preWsSyncSize = Y.encodeStateAsUpdateV2(doc).byteLength;
            provider.connect();
          });

          // After WS sync, check if the merged doc grew significantly beyond
          // what IDB had. Large growth means IDB was very stale (e.g. user was
          // offline while others rewrote the document). In that case, clear and
          // re-seed IDB so it doesn't accumulate divergent tombstone history.
          let staleCheckDone = false;
          provider.once('sync', () => {
            if (staleCheckDone) return;
            staleCheckDone = true;
            try {
              const postSyncSize = Y.encodeStateAsUpdateV2(doc).byteLength;
              const growth = postSyncSize - preWsSyncSize;
              const growthRatio = preWsSyncSize > 0 ? postSyncSize / preWsSyncSize : 1;
              if (growthRatio > 3 && growth > 50000) {
                log.warn(`[CRDT] IDB stale for ${roomId.slice(-8)}: ${preWsSyncSize}b → ${postSyncSize}b (${growthRatio.toFixed(1)}x). Clearing IDB to reseed.`);
                void (idb as any).clearData();
              }
            } catch (err) {
              log.error(`[CRDT] Stale IDB check failed for ${roomId.slice(-8)}:`, err);
            }
          });
        }
        
        // Single unified status handler
        provider.on('status', ({ status }: { status: string }) => {
          if (roomId.startsWith('doc:')) {
            const yxml = doc.getXmlFragment('prosemirror');
            log.log(`[CRDT-WS] ${roomId.slice(-8)} ${status} elements=${yxml.length}`);
          }
          if (status === 'connected') {
            // Ensure binary frame type is set on every (re)connection
            if (provider.ws) provider.ws.binaryType = 'arraybuffer';
            log.log(`[CRDT-WS] Connected to ${roomId}`);
          } else if (status === 'disconnected') {
            log.warn(`[CRDT-WS] Disconnected from ${roomId}, will auto-reconnect`);
          }
        });

        // Set binaryType immediately on creation (before first status event)
        if (provider.ws) provider.ws.binaryType = 'arraybuffer';
        
        provider.on('connection-error', (event: Event) => {
          log.error(`[CRDT-WS] Connection error for ${roomId}:`, event);
          // The provider will automatically retry connections
        });
        
        provider.on('connection-close', (event: CloseEvent | null) => {
          if (event) {
            log.warn(`[CRDT-WS] Connection closed for ${roomId}:`, event.code, event.reason);
          } else {
            log.warn(`[CRDT-WS] Connection closed for ${roomId}`);
          }
        });
        
        // 🛡️ CRITICAL: Add Y.Doc error handler to catch internal decoding errors
        // This prevents "Unexpected end of array" errors from crashing the app
        // when corrupted binary data is received from WebSocket
        // @ts-expect-error - Y.Doc does support 'error' event but TypeScript types are incomplete
        doc.on('error', (error: Error) => {
          log.error(`[CRDT-WS] 🚨 Y.Doc internal error for ${roomId}:`, error.message);
          
          // Check if this is a decoding/corruption error
          const isDecodingError = error.message?.includes('Unexpected end of array') ||
                                  error.message?.includes('decoding') ||
                                  error.message?.includes('decoder') ||
                                  error.message?.includes('corrupt');
          
          if (isDecodingError) {
            log.error(`[CRDT-WS] 🚨 CORRUPTED DATA detected for ${roomId}`);
            log.error(`[CRDT-WS] This usually means the WebSocket server sent invalid Y.js binary data`);
            log.warn(`[CRDT-WS] 🔄 Disconnecting and clearing cache to force fresh load...`);
            
            // Disconnect corrupted provider to stop receiving more bad data
            try {
              provider.disconnect();
              log.log(`[CRDT-WS] ✅ Disconnected corrupted provider for ${roomId}`);
            } catch (disconnectError) {
              log.error(`[CRDT-WS] Failed to disconnect provider:`, disconnectError);
            }
            
            // Clear from cache to force fresh load on next access
            this.docs.delete(key);
            this.providers.delete(key);
            log.log(`[CRDT-WS] ✅ Cleared ${roomId} from cache - will reload fresh on next access`);
            
            // Log instructions for manual recovery if needed
            log.warn(`[CRDT-WS] 📋 Recovery steps:`);
            log.warn(`[CRDT-WS]   1. Check WebSocket server logs for corruption details`);
            log.warn(`[CRDT-WS]   2. Verify database integrity for: ${roomId}`);
            log.warn(`[CRDT-WS]   3. Refresh the page to reload document`);
          }
        });
        
        this.providers.set(key, provider);
        wsTokenRefresher.register(provider);
        log.log(`✅ [WS_TRACE:${wsTraceId}] Provider stored in map`);
        log.log(`[CRDT-WS] Created provider for ${roomId}. Totals => docs:${this.docs.size}, providers:${this.providers.size}`);
      } catch (error) {
        log.error(`❌ [WS_TRACE:${wsTraceId}] Provider creation failed:`, error);
        log.error(`[CRDT-WS] Failed to create WebSocket provider for ${roomId}:`, error);
        // Continue without WebSocket - the doc will still work locally
      }
    } else {
      log.log(`✅ [WS_TRACE:${wsTraceId}] Provider already exists, skipping creation`);
    }
    
    log.log(`🟢 [WS_TRACE:${wsTraceId}] === CRDT-WS.ENSUREINITIALIZED COMPLETE ===`, {
      hasDoc: !!doc,
      docClientId: doc.clientID,
      hasProvider: this.providers.has(key),
      totalDocs: this.docs.size,
      totalProviders: this.providers.size
    });
    
    return doc;
  }

  // Function to get the appropriate debounce time based on key
  private getDebounceTime(key: string): number {
    // Check if the key contains any of our special types
    for (const keyType of Object.keys(CRDT_KEY_CONFIGS)) {
      if (keyType !== 'default' && key.includes(keyType)) {
        return CRDT_KEY_CONFIGS[keyType];
      }
    }
    // Fall back to default
    return CRDT_KEY_CONFIGS.default;
  }

  private debouncedObserver(key: string, ymap: Y.Map<any>, onUpdate: (update: any) => void) {
    // Create a debounced version of the observer that waits a short period before firing
    let timeoutId: any = null;
    let lastUpdate: any = null;
    
    // Determine the debounce time based on the key type
    const debounceTime = this.getDebounceTime(key);
    
    return (event: Y.YMapEvent<any>, transaction: Y.Transaction) => {
      if (transaction.origin === this.localOriginId) {
        return;
      }
      
      const data = this.ymapToObject(ymap);
      if (!data) {
        return;
      }
      
      lastUpdate = {
        type: 'update',
        data: data,
        timestamp: Date.now(),
      };
      
      // For zero-debounce items, send immediately
      if (debounceTime === 0) {
        try {
          onUpdate(lastUpdate);
        } catch (error) {
          log.error(`[CRDT-WS] Error in immediate update callback for key: ${key}:`, error);
        }
        return;
      }
      
      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      // Set a new timeout with the appropriate debounce time
      timeoutId = setTimeout(() => {
        try {
          onUpdate(lastUpdate);
        } catch (error) {
          log.error(`[CRDT-WS] Error in debounced update callback for key: ${key}:`, error);
        }
        timeoutId = null;
      }, debounceTime);
    };
  }

  connect<T>(key: string, onUpdate: (update: CRDTUpdate<T>) => void): () => void {
    const doc = this.ensureInitialized(key);

    const ymap = doc.getMap<T>(key);
    this.states.set(key, ymap);

    const debouncedObserver = this.debouncedObserver(key, ymap, onUpdate);
    
    ymap.observe(debouncedObserver);

    this.connections.set(key, onUpdate);
    this.observers.set(key, {
      observer: debouncedObserver,
      cleanup: () => {
        ymap.unobserve(debouncedObserver);
        this.connections.delete(key);
        this.observers.delete(key);
      },
    });

    return () => {
      const observer = this.observers.get(key);
      if (observer) {
        observer.cleanup();
      }
    };
  }

  update<T extends object>(
    key: string,
    newData: T,
    options: {
      preserveOtherConnections?: boolean;
      preserveNoteConnections?: boolean;
      updateType?: 'update' | 'remove';
    } = {},
  ) {
    try {
      const doc = this.ensureInitialized(key);
      const ymap = doc.getMap(key);
      const updateMap = this.objectToYmap<T>(newData);

      doc.transact(() => {
        if (options.updateType === 'remove') {
          this.removeMapEntries(ymap, updateMap);
        } else if (options.updateType === 'update') {
          this.updateMap(ymap, updateMap);
        }
      }, this.localOriginId);

      this.states.set(key, ymap);

      if (!options.preserveOtherConnections) {
        this.cleanupObservers(key, options);
      }
    } catch (error) {
      log.error(`[CRDT-WS] Error in update method for key: ${key}:`, error);
      throw error;
    }
  }

  disconnect(key: string) {
    const provider = this.providers.get(key);
    if (provider) {
      try {
        // Clear local awareness state before closing
        try { (provider as any).awareness?.setLocalState(null); } catch {}
        // Prefer destroy() for a hard close to avoid lingering server peers
        if (typeof (provider as any).destroy === 'function') {
          (provider as any).destroy();
        } else {
          provider.disconnect();
        }
      } catch {}
      wsTokenRefresher.unregister(provider);
      this.providers.delete(key);
    }
    const idb = this.indexedDbs.get(key);
    if (idb) {
      try { idb.destroy(); } catch {}
      this.indexedDbs.delete(key);
      idbManifest.unregister(key);
    }
    this.docs.delete(key);
    log.log(`[CRDT-WS] Disconnected and removed key ${key}. Totals => docs:${this.docs.size}, providers:${this.providers.size}`);
  }

  cleanup() {
    // Copy keys to avoid iterator mutation issues
    const keys = Array.from(this.providers.keys());
    for (const key of keys) {
      this.disconnect(key); // disconnect() handles idb.destroy() + idbManifest.unregister()
    }
  }

  // DEBUG HELPERS

  getProvider(key: string): WebsocketProvider | undefined {
    return this.providers.get(key);
  }

  connectProvider(key: string): void {
    const provider = this.providers.get(key);
    if (provider && !provider.wsconnected) {
      provider.connect();
      log.log(`[CRDT-WS] Manually connected provider for ${key}`);
    }
  }

  /** Force-reconnect every provider that is currently disconnected. */
  reconnectAll(): void {
    let reconnected = 0;
    for (const [key, provider] of this.providers.entries()) {
      if (!provider.wsconnected) {
        provider.connect();
        reconnected++;
        log.log(`[CRDT-WS] Reconnecting provider for ${key}`);
      }
    }
    if (reconnected > 0) {
      log.log(`[CRDT-WS] Reconnected ${reconnected} disconnected provider(s)`);
    }
  }

  getActiveDocs(): Map<string, Y.Doc> {
    return this.docs;
  }

  getActiveProviders(): Map<string, WebsocketProvider> {
    return this.providers;
  }

  // Emergency cleanup: remove all docs/providers for a specific doc id
  cleanupDocId(docId: string) {
    const key = `doc:${docId}`;
    try {
      this.disconnect(key);
      log.log(`[CRDT-WS] cleanupDocId: force disconnected ${key}`);
    } catch (e) {
      log.error('[CRDT-WS] cleanupDocId error:', e);
    }
  }

  get(key: string) {
    return this.docs.get(key);
  }

  private updateMap(ymap: Y.Map<any>, updateMap: Map<string, any>) {
    for (const [k, v] of updateMap.entries()) {
      try {
        if (ymap.get(k) !== v) {
          ymap.set(k, v);
        }
      } catch (keyError) {
        log.error('[CRDT-WS] map: Error setting key:', k, 'Error:', keyError);
        // Continue with other keys even if one fails
      }
    }
  }

  private removeMapEntries(ymap: Y.Map<any>, updateMap: Map<string, any>) {
    for (const [k, v] of updateMap.entries()) {
      // Special handling for null values - explicitly delete the key
      if (v === null || v === undefined) {
        if (ymap.has(k)) {
          ymap.delete(k);
        }
        continue;
      }
      
      // For other patterns, look for matching keys or patterns
      ymap.forEach((value, mapKey) => {
        if (mapKey === k || mapKey.startsWith(k + '.')) {
          ymap.delete(mapKey);
        }
      });
    }
  }

  private cleanupObservers(key: string, options: { preserveNoteConnections?: boolean }) {
    for (const [connKey, observer] of this.observers.entries()) {
      if (connKey === key || (!options.preserveNoteConnections && !connKey.startsWith('note-'))) {
        observer.cleanup();
      }
    }
  }

  //
  // CRDT YMAP HELPERS
  //
  private ymapToObject<T>(ymap: Y.Map<T>): T | null {
    try {
      const ymapObject = ymap.toJSON();
      if (!ymapObject || typeof ymapObject !== 'object' || Object.keys(ymapObject).length === 0) {
        return null;
      }

      // Deep convert nested Y.js objects to plain JS — toJSON() only converts
      // the top-level Y.Map; nested Y.Maps need explicit recursive conversion.
      const deepConvert = (obj: any): any => {
        if (obj === null || obj === undefined) return obj;
        if (typeof obj !== 'object') return obj;
        if (obj.toJSON && typeof obj.toJSON === 'function') {
          return deepConvert(obj.toJSON());
        }
        if (Array.isArray(obj)) {
          return obj.map(deepConvert);
        }
        const converted: any = {};
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            converted[key] = deepConvert(obj[key]);
          }
        }
        return converted;
      };

      const fullyConverted = deepConvert(ymapObject);
      const normalizedObject = this.removeParentEntries(fullyConverted);
      return unflatten(normalizedObject) as T;
    } catch (error) {
      log.error(`[CRDT-WS] Error in ymapToObject:`, error);
      return null;
    }
  }

  private objectToYmap<T>(object: T) {
    try {
      const flatObject = flatten(object);
      const result = new Map(Object.entries(flatObject as Record<string, any>));
      return result;
    } catch (error) {
      log.error(`[CRDT-WS] Error in objectToYmap:`, error);
      throw error;
    }
  }

  private removeParentEntries(object: any) {
    const keys = Object.keys(object);
    const parentKeys = keys.filter((key) => {
      return keys.some((k) => k.startsWith(key + '.'));
    });
    parentKeys.forEach((key) => {
      delete object[key];
    });
    return object;
  }

  /** Returns a snapshot of all active Y.Doc rooms for the debug panel. */
  public getDebugSnapshot(): Array<{
    room: string;
    wsConnected: boolean;
    wsSynced: boolean;
    wsUrl: string;
    docKeys: string[];
  }> {
    const result: ReturnType<typeof this.getDebugSnapshot> = [];
    for (const [room, doc] of this.docs.entries()) {
      const provider = this.providers.get(room);
      const docKeys: string[] = [];
      doc.share.forEach((_val, key) => docKeys.push(key));
      result.push({
        room,
        wsConnected: provider?.wsconnected ?? false,
        wsSynced: provider?.synced ?? false,
        wsUrl: (provider as any)?.url ?? '',
        docKeys,
      });
    }
    return result;
  }
}

// Export a function to get the singleton instance
export function getCRDTServiceInstance(): CRDTServiceWS {
  if (!_instance) {
    _instance = new CRDTServiceWS();
  }
  return _instance;
}

// Add the missing export that's being imported elsewhere
export const crdtServiceWS = getCRDTServiceInstance();
